import React, { useMemo, useState } from 'react';
import { posilaPrijemciLogLabel } from '../../utils/posilaPrijemci';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Button,
    Grid,
    CircularProgress,
    Chip,
    Divider,
    Link,
    List,
    ListItem,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    FormControlLabel,
    Checkbox,
    Radio,
    RadioGroup,
    IconButton,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { csCZ } from '@mui/x-data-grid/locales';
import {
    ArrowBack as ArrowBackIcon,
    OpenInNew as OpenInNewIcon,
    Download as DownloadIcon,
    PictureAsPdf as PdfIcon,
    Delete as DeleteIcon,
    Cancel as CancelIcon,
    Send as SendIcon,
    Print as PrintIcon,
    PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { useUdalostDetailQuery, useDeleteUdalostMutation, useDeaktivujUdalostMutation, useAktivujUdalostMutation, useResendNotifikaceMutation, usePrintStatusQuery, usePrintUdalostMutation, useAllUsersQuery, useAppConfig } from '../../api/JsdhNotifikaceService/service/AdminMutations';
import { useObsazeniProPoplachQuery } from '../../api/JsdhNotifikaceService/service/ObsazeniHooks';
import { useOsa } from '../../api/JsdhNotifikaceService/service/ZasahMutations';
import ObsazeniVysledekView from '../../components/obsazeni/ObsazeniVysledekView';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useAuth } from '../../context/AuthContext';
import { PageHeader } from '../../components/admin';
import UserSelector from '../../components/UserSelector';
import MapZoomWrapper from '../../components/MapZoomWrapper';
import StatistikyReakceCard from '../../components/StatistikyReakceCard';
import OdpovediGrid from '../../components/OdpovediGrid';
import TimelineChart from '../../components/TimelineChart';
import VerticalTimeline from '../../components/VerticalTimeline';
import CasovaOsa from '../../components/zasah/CasovaOsa';
import { vypocitejStatistiky } from '../../utils/statistikyUtils';
import { useQuery } from '@tanstack/react-query';
import { exportToCSV } from '../../utils/exportUtils';
import { getSafeUrl } from '../../utils/utils';
import JsdhNotifikaceService from '../../api/JsdhNotifikaceService/service/JsdhNotifikaceService';
import type { PosilovaNotifikaceLogResponse } from '../../api/JsdhNotifikaceService/generated';
import { castJinaNezObec, formatAdresa } from '../../utils/adresaUdalosti';

const DetailUdalostiPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const udalostId = parseInt(id || '0', 10);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
    const [activateDialogOpen, setActivateDialogOpen] = useState(false);
    const [resendDialogOpen, setResendDialogOpen] = useState(false);
    const [resendWebPush, setResendWebPush] = useState(true);
    const [resendFcm, setResendFcm] = useState(true);
    const [resendUserIds, setResendUserIds] = useState<number[] | null>(null); // null = všem

    // Activation dialog state
    const [activateCreateNew, setActivateCreateNew] = useState(false);
    const [activateSendNotifications, setActivateSendNotifications] = useState(true);
    const [activateGenerateTts, setActivateGenerateTts] = useState(true);

    const {
        data: detail,
        isLoading,
        isError,
    } = useUdalostDetailQuery(udalostId);

    const { data: notifikaceLog } = useQuery<PosilovaNotifikaceLogResponse[]>({
        queryKey: ['udalost-notifikace-log', udalostId],
        queryFn: () => JsdhNotifikaceService.getNotifikaceLog(udalostId),
        enabled: udalostId > 0,
    });

    const deleteMutation = useDeleteUdalostMutation();
    const deactivateMutation = useDeaktivujUdalostMutation();
    const activateMutation = useAktivujUdalostMutation();
    const resendMutation = useResendNotifikaceMutation();
    const printMutation = usePrintUdalostMutation();

    // Users for recipient selection
    const { data: allUsers = [], isLoading: isLoadingUsers } = useAllUsersQuery();

    const { data: printStatus } = usePrintStatusQuery(udalostId);

    const statistiky = useMemo(() => {
        if (!detail?.timeline) return null;
        return vypocitejStatistiky(detail.timeline);
    }, [detail?.timeline]);

    const nereagovali = useMemo(() => {
        if (!allUsers.length || !detail?.timeline) return [];
        const hasUzivatelIds = detail.timeline.some(t => t.uzivatelId != null);
        if (!hasUzivatelIds) return [];
        const respondedIds = new Set(
            detail.timeline.map(t => t.uzivatelId).filter((id): id is number => id != null)
        );
        return allUsers.filter(u => u.aktivni && !respondedIds.has(u.id));
    }, [allUsers, detail?.timeline]);

    const handleDelete = () => {
        deleteMutation.mutate(udalostId, {
            onSuccess: () => {
                navigate('/admin/udalosti');
            },
        });
        setDeleteDialogOpen(false);
    };

    const handleDeactivate = () => {
        deactivateMutation.mutate(udalostId, {
            onSuccess: () => {
                setDeactivateDialogOpen(false);
            },
        });
    };

    const handleActivate = () => {
        activateMutation.mutate(
            {
                id: udalostId,
                vytvorNovouUdalost: activateCreateNew,
                odeslatNotifikace: activateSendNotifications,
                generovatTts: activateCreateNew ? activateGenerateTts : false,
            },
            {
                onSuccess: (result) => {
                    setActivateDialogOpen(false);
                    // If a new event was created, navigate to its detail
                    if (activateCreateNew && result.udalost?.id && result.udalost.id !== udalostId) {
                        navigate(`/admin/udalosti/${result.udalost.id}`);
                    }
                },
            }
        );
    };

    const handleResendNotifikace = () => {
        resendMutation.mutate(
            { id: udalostId, webPush: resendWebPush, fcm: resendFcm, userIds: resendUserIds },
            {
                onSuccess: () => {
                    setResendDialogOpen(false);
                    setResendUserIds(null);
                },
            }
        );
    };

    const handleCloseResendDialog = () => {
        setResendDialogOpen(false);
        setResendUserIds(null);
    };

    const handlePrint = () => {
        printMutation.mutate(udalostId);
    };

    const { enabled: obsazeniFlag } = useFeatureFlag('FEATURE_OBSAZOVANI_VOZIDEL');
    const [zobrazitObsazeni, setZobrazitObsazeni] = useState(false);
    const obsazeni = useObsazeniProPoplachQuery(udalostId, obsazeniFlag && zobrazitObsazeni && udalostId > 0);

    // HO-151 Task 1: casova osa zaznamu zasahu nahrazuje dnesni "Prubeh udalosti",
    // kdyz je zaznam zasahu (HO-148) i chat (HO-143/144) pro jednotku zapnuty
    // A zaroven ma jednotka ulozene evidencni cislo (ulozisteReady) - bez toho
    // BE dokumentaci a chat nezobrazi. Gate je AND vsech tri - jednotka s
    // vypnutou/nehotovou castí nesmi prijit ani o dnesni Prubeh udalosti.
    const { enabled: zaznamZasahuFlag } = useFeatureFlag('MODUL_ZAZNAM_ZASAHU');
    const { enabled: chatFlag } = useFeatureFlag('MODUL_CHAT');
    const { data: appConfig } = useAppConfig();
    const { isAdmin } = useAuth();
    const showCasovaOsa = zaznamZasahuFlag && chatFlag && !!appConfig?.ulozisteReady;
    // Dotaz se pousti jen kdyz je osa opravdu videt. Drive tu stalo „hook se vola
    // vzdy (pravidla hooku)" - to je zamena pojmu: hook se skutecne VOLAT musi,
    // ale `enabled` to reseni ma a o par radku vys ho tenhle soubor pouziva
    // (`useObsazeniProPoplachQuery`). Bez nej sel dotaz na osu i u jednotek
    // s vypnutym modulem a u detailu bez id na `/api/poplach/0/osa`.
    const osaQuery = useOsa(udalostId, showCasovaOsa && udalostId > 0);

    const [pdfLoading, setPdfLoading] = useState(false);
    const handleDownloadPdf = async () => {
        setPdfLoading(true);
        try {
            await JsdhNotifikaceService.downloadPdf(udalostId);
        } catch {
            // error handled silently - download fails gracefully
        } finally {
            setPdfLoading(false);
        }
    };

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (isError || !detail) {
        return (
            <Box>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/admin/udalosti')}
                    sx={{ mb: 2 }}
                >
                    Zpět na seznam
                </Button>
                <Typography color="error">
                    Nepodařilo se načíst detail události.
                </Typography>
            </Box>
        );
    }

    // HO-161: hlavní řádek = adresa (obec, ulice č. p.); doplňky jen to, co v ní není.
    const adresa = formatAdresa(detail);
    const castMimoAdresu = detail.ulice && castJinaNezObec(detail.obec, detail.cast) ? detail.cast : null;
    const uzemi = [
        castMimoAdresu ? `část ${castMimoAdresu}` : null,
        detail.okres ? `okr. ${detail.okres}` : null,
        detail.kraj,
    ].filter(Boolean);

    return (
        <Box>
            <PageHeader
                title={detail.udalost || 'Detail události'}
                icon={
                    <IconButton onClick={() => navigate('/admin/udalosti')}>
                        <ArrowBackIcon />
                    </IconButton>
                }
                action={
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Chip
                            label={detail.aktivni ? 'Aktivní' : 'Ukončená'}
                            color={detail.aktivni ? 'success' : 'default'}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => exportToCSV(detail)}
                        >
                            CSV
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={pdfLoading ? <CircularProgress size={16} /> : <PdfIcon />}
                            onClick={handleDownloadPdf}
                            disabled={pdfLoading}
                        >
                            {pdfLoading ? 'Generuji...' : 'PDF'}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            color="secondary"
                            startIcon={<PrintIcon />}
                            onClick={handlePrint}
                            disabled={!printStatus?.printAvailable || printStatus?.alreadyQueued || printMutation.isPending}
                            title={
                                !printStatus?.printAvailable
                                    ? 'Tisk není dostupný - žádné zařízení s povoleným tiskem'
                                    : printStatus?.alreadyQueued
                                        ? 'Tisk již byl zařazen do fronty'
                                        : `Tisknout na ${printStatus?.enabledDevicesCount} zařízení`
                            }
                        >
                            {printMutation.isPending ? 'Odesílám...' : printStatus?.alreadyQueued ? 'Ve frontě' : 'Tisknout'}
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            color="info"
                            startIcon={<SendIcon />}
                            onClick={() => setResendDialogOpen(true)}
                        >
                            Znovu odeslat
                        </Button>
                        {detail.aktivni ? (
                            <Button
                                variant="outlined"
                                size="small"
                                color="warning"
                                startIcon={<CancelIcon />}
                                onClick={() => setDeactivateDialogOpen(true)}
                            >
                                Deaktivovat
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                size="small"
                                color="success"
                                startIcon={<PlayArrowIcon />}
                                onClick={() => setActivateDialogOpen(true)}
                            >
                                Aktivovat
                            </Button>
                        )}
                        {obsazeniFlag && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setZobrazitObsazeni((p) => !p)}
                                endIcon={<Chip label="BETA" color="warning" size="small" variant="outlined" />}
                            >
                                Rozřazení do vozidel
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => setDeleteDialogOpen(true)}
                        >
                            Smazat
                        </Button>
                    </Box>
                }
            />

            {/* Hlavicka s typem poplachu */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                    <Box>
                        <Typography variant="h3" component="h1" fontWeight="bold" color="error.main">
                            {detail.pred?.toUpperCase() || 'POPLACH'}
                        </Typography>
                        <Typography variant="h5" color="text.secondary">
                            {detail.udalost}
                        </Typography>
                    </Box>
                    <Chip
                        label={detail.aktivni ? 'Aktivní' : 'Ukončená'}
                        color={detail.aktivni ? 'success' : 'default'}
                        size="medium"
                        sx={{ fontSize: '1rem', py: 2, px: 1 }}
                    />
                </Box>
            </Paper>

            {/* Zakladni informace */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Informace o události
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        {detail.objekt && (
                            <Typography variant="body1" gutterBottom>
                                <strong>Objekt:</strong> {detail.objekt}
                            </Typography>
                        )}
                        {detail.popis && (
                            <Typography variant="body1" gutterBottom>
                                <strong>Popis:</strong> {detail.popis}
                            </Typography>
                        )}
                        {detail.upresneni && (
                            <Typography variant="body1" gutterBottom>
                                <strong>Upřesnění:</strong> {detail.upresneni}
                            </Typography>
                        )}
                        {adresa && (
                            <Typography variant="body1" gutterBottom>
                                <strong>Adresa:</strong> {adresa}
                            </Typography>
                        )}
                        {uzemi.length > 0 && (
                            <Typography variant="body1" gutterBottom>
                                <strong>Území:</strong> {uzemi.join(', ')}
                            </Typography>
                        )}
                        <Typography variant="body1" gutterBottom>
                            <strong>Čas vyhlášení:</strong>{' '}
                            {new Date(detail.casVyhlaseni).toLocaleString('cs-CZ')}
                        </Typography>
                        {detail.vyhlasilJmeno && (
                            <Typography variant="body1" gutterBottom>
                                <strong>Vyhlásil:</strong> {detail.vyhlasilJmeno}
                            </Typography>
                        )}
                        {detail.oznamovatel && (
                            <Typography variant="body1" gutterBottom>
                                <strong>Oznamovatel:</strong> {detail.oznamovatel}
                            </Typography>
                        )}
                        {detail.telefonOznamovatele && (
                            <Typography variant="body1" gutterBottom>
                                <strong>Telefon:</strong>{' '}
                                <Link href={`tel:${detail.telefonOznamovatele}`}>{detail.telefonOznamovatele}</Link>
                            </Typography>
                        )}
                        {detail.cisloUdalosti && (
                            <Typography variant="body1" gutterBottom>
                                <strong>Číslo události:</strong> {detail.cisloUdalosti}
                            </Typography>
                        )}
                        {detail.odbavil && (
                            <Typography variant="body1" gutterBottom>
                                <strong>Odbavil:</strong> {detail.odbavil}
                            </Typography>
                        )}
                    </Grid>

                    {/* Technika */}
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Grid container spacing={2}>
                            {detail.technikaJednotky && detail.technikaJednotky.length > 0 && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                        Technika jednotky:
                                    </Typography>
                                    <List dense disablePadding>
                                        {detail.technikaJednotky.map((t, i) => (
                                            <ListItem key={`technika-${i}-${t}`} disablePadding sx={{ py: 0.25 }}>
                                                <ListItemText primary={t} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Grid>
                            )}
                            {detail.dalsiTechnikaJednotek && detail.dalsiTechnikaJednotek.length > 0 && (
                                <Grid size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                        Další technika:
                                    </Typography>
                                    <List dense disablePadding>
                                        {detail.dalsiTechnikaJednotek.map((t, i) => (
                                            <ListItem key={`dalsi-technika-${i}-${t}`} disablePadding sx={{ py: 0.25 }}>
                                                <ListItemText primary={t} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            {obsazeniFlag && zobrazitObsazeni && (
                <Paper sx={{ p: 2, mt: 2 }} data-testid="obsazeni-poplach">
                    <Typography variant="h6" gutterBottom>
                        Rozřazení do vozidel (beta)
                    </Typography>
                    {obsazeni.isLoading && <Typography variant="body2">Načítám…</Typography>}
                    {obsazeni.isError && (
                        <Typography variant="body2" color="error">
                            Nepodařilo se načíst návrh obsazení.
                        </Typography>
                    )}
                    {obsazeni.data && <ObsazeniVysledekView data={obsazeni.data} />}
                </Paper>
            )}

            {/* Mapa */}
            {detail.gps && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Mapa</Typography>
                        <Box display="flex" gap={1}>
                            {getSafeUrl(detail.googleMapLink) && (
                                <Button
                                    component="a"
                                    size="small"
                                    variant="outlined"
                                    endIcon={<OpenInNewIcon />}
                                    href={getSafeUrl(detail.googleMapLink)!}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Google Maps
                                </Button>
                            )}
                            {getSafeUrl(detail.mapyCzLink) && (
                                <Button
                                    component="a"
                                    size="small"
                                    variant="outlined"
                                    endIcon={<OpenInNewIcon />}
                                    href={getSafeUrl(detail.mapyCzLink)!}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Mapy.cz
                                </Button>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ height: 400 }}>
                        <MapZoomWrapper
                            gps={detail.gps}
                            hasCisloPopisne={!!detail.cisloPopisne}
                            showHyndrant={detail.pred === 'POZAR'}
                            height="100%"
                        />
                    </Box>
                </Paper>
            )}

            {/* Odpovědi hasičů (s počty jako header) */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Odpovědi hasičů
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box display="flex" gap={1} mb={2} flexWrap="wrap" sx={{ '& .MuiChip-root': { fontSize: '0.875rem', height: 32 } }}>
                    <Chip label={`JEDU: ${detail.pocetJedu}`} color="success" />
                    <Chip label={`NEJEDU: ${detail.pocetNejedu}`} color="error" />
                    {nereagovali.length > 0 && (
                        <Chip label={`Nereagoval: ${nereagovali.length}`} variant="outlined" />
                    )}
                </Box>
                {detail.timeline && detail.timeline.length > 0 ? (
                    <OdpovediGrid
                        timeline={detail.timeline}
                        showDobaReakce
                        nejrychlejsiIds={statistiky?.nejrychlejsiIds}
                        nereagovali={nereagovali}
                    />
                ) : (
                    <Typography color="text.secondary">
                        Zatím žádné odpovědi.
                    </Typography>
                )}
            </Paper>

            {/* Prubeh udalosti / Prubeh zasahu (HO-151 Task 1 gating) */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    {showCasovaOsa ? 'Průběh zásahu' : 'Průběh události'}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {showCasovaOsa ? (
                    osaQuery?.isLoading ? (
                        <Box display="flex" justifyContent="center" p={2}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : osaQuery?.data ? (
                        <CasovaOsa
                            poplachId={udalostId}
                            data={osaQuery.data}
                            nejrychlejsiIds={statistiky?.nejrychlejsiIds}
                            isAdmin={isAdmin}
                        />
                    ) : (
                        <Typography color="text.secondary">
                            Nepodařilo se načíst časovou osu.
                        </Typography>
                    )
                ) : (
                    <VerticalTimeline
                        timeline={detail.timeline || []}
                        casVyhlaseni={detail.casVyhlaseni}
                        nejrychlejsiIds={statistiky?.nejrychlejsiIds}
                    />
                )}
            </Paper>

            {/* Statistiky a graf */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Statistiky a graf
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <StatistikyReakceCard statistiky={statistiky} />
                {detail.timeline && detail.timeline.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                        <TimelineChart timeline={detail.timeline} />
                    </Box>
                )}
            </Paper>

            {/* Odeslané notifikace */}
            {notifikaceLog && notifikaceLog.length > 0 && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Odeslané notifikace
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <DataGrid
                        rows={notifikaceLog}
                        columns={[
                            {
                                field: 'createdAt',
                                headerName: 'Čas',
                                width: 170,
                                valueFormatter: (value: string) =>
                                    new Date(value).toLocaleString('cs-CZ'),
                            },
                            {
                                field: 'odeslalJmeno',
                                headerName: 'Odesílatel',
                                width: 150,
                                valueFormatter: (value: string) => value || '—',
                            },
                            {
                                field: 'text',
                                headerName: 'Text',
                                flex: 1,
                                minWidth: 200,
                                valueFormatter: (value: string) => value || '—',
                            },
                            {
                                field: 'recipients',
                                headerName: 'Příjemci',
                                width: 120,
                                valueFormatter: (value: string) => posilaPrijemciLogLabel(value),
                            },
                            {
                                field: 'pocetOdeslanych',
                                headerName: 'Počet',
                                width: 80,
                                type: 'number',
                            },
                            {
                                field: 'kriticka',
                                headerName: 'Kritická',
                                width: 100,
                                renderCell: (params) => params.value ? (
                                    <Chip label="ANO" color="error" size="small" />
                                ) : (
                                    <Chip label="NE" size="small" variant="outlined" />
                                ),
                            },
                        ] as GridColDef[]}
                        pageSizeOptions={[5, 10]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 5 } },
                        }}
                        disableRowSelectionOnClick
                        autoHeight
                        localeText={csCZ.components.MuiDataGrid.defaultProps.localeText}
                    />
                </Paper>
            )}

            {/* Dialog pro potvrzení smazání */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Smazat událost?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Opravdu chcete smazat událost "{detail.udalost}"? Tato akce je nevratná
                        a smaže i všechny odpovědi spojené s touto událostí.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Zrušit
                    </Button>
                    <Button
                        onClick={handleDelete}
                        color="error"
                        variant="contained"
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? 'Mažu...' : 'Smazat'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog pro potvrzení deaktivace */}
            <Dialog
                open={deactivateDialogOpen}
                onClose={() => setDeactivateDialogOpen(false)}
                aria-labelledby="deactivate-dialog-title"
                aria-describedby="deactivate-dialog-description"
            >
                <DialogTitle id="deactivate-dialog-title">
                    Deaktivovat poplach?
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="deactivate-dialog-description">
                        Opravdu chcete deaktivovat poplach "{detail.udalost}"?
                        Poplach bude označen jako ukončený a nebude se zobrazovat na dashboardu.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeactivateDialogOpen(false)}>
                        Zrušit
                    </Button>
                    <Button
                        onClick={handleDeactivate}
                        color="warning"
                        variant="contained"
                        disabled={deactivateMutation.isPending}
                    >
                        {deactivateMutation.isPending ? 'Deaktivuji...' : 'Deaktivovat'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog pro aktivaci události */}
            <Dialog
                open={activateDialogOpen}
                onClose={() => setActivateDialogOpen(false)}
                aria-labelledby="activate-dialog-title"
                aria-describedby="activate-dialog-description"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="activate-dialog-title">
                    Aktivovat událost
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="activate-dialog-description" sx={{ mb: 2 }}>
                        Vyberte způsob aktivace události "{detail.udalost}".
                    </DialogContentText>
                    <RadioGroup
                        value={activateCreateNew ? 'new' : 'reactivate'}
                        onChange={(e) => setActivateCreateNew(e.target.value === 'new')}
                    >
                        <FormControlLabel
                            value="reactivate"
                            control={<Radio />}
                            label="Reaktivovat starou událost (nastaví čas vyhlášení na aktuální)"
                        />
                        <FormControlLabel
                            value="new"
                            control={<Radio />}
                            label="Vytvořit novou událost (kopie dat)"
                        />
                    </RadioGroup>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={activateSendNotifications}
                                    onChange={(e) => setActivateSendNotifications(e.target.checked)}
                                />
                            }
                            label="Odeslat notifikace členům"
                        />
                        {activateCreateNew && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={activateGenerateTts}
                                        onChange={(e) => setActivateGenerateTts(e.target.checked)}
                                    />
                                }
                                label="Generovat TTS audio"
                            />
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setActivateDialogOpen(false)}>
                        Zrušit
                    </Button>
                    <Button
                        onClick={handleActivate}
                        color="success"
                        variant="contained"
                        disabled={activateMutation.isPending}
                    >
                        {activateMutation.isPending ? 'Aktivuji...' : 'Aktivovat'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog pro znovu odeslání notifikací */}
            <Dialog
                open={resendDialogOpen}
                onClose={handleCloseResendDialog}
                aria-labelledby="resend-dialog-title"
                aria-describedby="resend-dialog-description"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="resend-dialog-title">
                    Znovu odeslat notifikace
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="resend-dialog-description" sx={{ mb: 2 }}>
                        Vyberte, které notifikace chcete znovu odeslat pro událost "{detail.udalost}".
                    </DialogContentText>
                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={resendWebPush}
                                    onChange={(e) => setResendWebPush(e.target.checked)}
                                />
                            }
                            label="Web Push notifikace"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={resendFcm}
                                    onChange={(e) => setResendFcm(e.target.checked)}
                                />
                            }
                            label="FCM notifikace"
                        />
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <UserSelector
                        users={allUsers}
                        isLoading={isLoadingUsers}
                        selectedUserIds={resendUserIds}
                        onSelectionChange={setResendUserIds}
                        disabled={resendMutation.isPending}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseResendDialog}>
                        Zrušit
                    </Button>
                    <Button
                        onClick={handleResendNotifikace}
                        color="primary"
                        variant="contained"
                        disabled={resendMutation.isPending || (!resendWebPush && !resendFcm) || (resendUserIds !== null && resendUserIds.length === 0)}
                    >
                        {resendMutation.isPending ? 'Odesílám...' : 'Odeslat'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DetailUdalostiPage;
